import type { Signal } from '@preact/signals-react';
import type { Dispatch, Ref } from 'react';
import { forwardRef, useRef } from 'react';
import type { TypeOrArray } from '../../common-interfaces';
import type { ContinuousScrollerFilter } from '../../continuous-scroller/continuous-scroller-types';
import type { DataFilterRef } from '../../data/data-filter/data-filter.component';
import DataFilter from '../../data/data-filter/data-filter.component';
import type { DropDownOptionRendererRef } from '../../data/drop-down-option-renderer/drop-down-option-renderer.component';
import type { DynamicFieldDataProps } from '../../dynamic-form/dynamic-form.interfaces';
import { DataSearchLocation } from '../../enums';
import type { JsonData, JsonDataItem } from '../../generic-types';
import type { ActionSectionItem } from './drop-down-action-section.component';
import { DropDownActionSection } from './drop-down-action-section.component';
import { DropDownFeatures } from './drop-down-features';
import DropDownOptionList from './drop-down-option-list.component';
import type { DropDownOptionListTemplateType, DropDownOptionTemplateType, DropDownResult } from './drop-down-types';
import { DropDownSelectionType } from './drop-down-types';
import type { DropDownReducerAction } from './drop-down.reducer';
import type { OdinKey } from '../../../common/types/odin-key.type';

export interface ActionSectionItemData extends ActionSectionItem {
  id: OdinKey;
}

export interface SelectAllSettings {
  checked: boolean;
  partial: boolean;
}

export interface DropDownOptionWrapperProps extends DynamicFieldDataProps {
  id: OdinKey;
  value?: Array<DropDownResult>;
  allowSearch?: boolean;
  setDefaultValue: boolean;
  selectionType?: DropDownSelectionType;
  useCheckboxForMultiSelect?: boolean;
  /**
   * When using this setting useCheckboxForMultiSelect should be set to false.I would prefer to have some kind of
   * function that would allow the calling component to determine what to do on each option component render but that
   * seems highly inefficient (especially since the item being passed to the function would be JSX and would not be able
   * to be changed). So I'm using this setting to tell the component to remove any options in the currently selected
   * values when isMultiSelect is set to true.
   */
  removeOnSelect?: boolean;
  isListOpen: boolean;
  actionItems?: Array<ActionSectionItem>;
  itemsToRemove?: Array<DropDownResult>;
  placeholder?: string;
  filterSignal: Signal<Array<ContinuousScrollerFilter>>;
  filterLocation?: DataSearchLocation;
  registerSignal?: Signal<boolean>;
  dispatcher: Dispatch<DropDownReducerAction>;
  onActionItemChanged?: (item: ActionSectionItemData, features: DropDownFeatures) => void;
  onChange?: (item?: TypeOrArray<DropDownResult>) => void;
  onDoubleClick?: (item: DropDownResult) => void;
  optionListStyle?: string;
  optionHoverStyle?: string;
  optionSelectedStyle?: string;
  optionTemplate?: DropDownOptionTemplateType;
  optionListTemplate?: DropDownOptionListTemplateType;
  dropDownDataLoaded?: (page: number, data: JsonData) => void;
  showAddNewOption?: boolean;
  debounceTime?: number;
  allowDuplicateCall?: boolean;
  filterInputRef?: Ref<DataFilterRef>;
  onNewItemCreated?: (value: JsonDataItem) => void;
  showDataOnSearch?: boolean;
  minimumSearchCharacters?: number;
}

const DropDownOptionWrapper = (props: DropDownOptionWrapperProps, ref: Ref<DropDownOptionRendererRef>) => {
  const {
    id,
    textField,
    valueField,
    archivedField,
    data,
    value,
    allowSearch = true,
    setDefaultValue,
    selectionType = DropDownSelectionType.Single,
    useCheckboxForMultiSelect = true,
    removeOnSelect = false,
    isListOpen,
    actionItems = [],
    itemsToRemove,
    placeholder,
    filterSignal,
    filterLocation = DataSearchLocation.Local,
    registerSignal,
    dispatcher,
    onActionItemChanged,
    onChange,
    onDoubleClick,
    optionListStyle,
    optionHoverStyle,
    optionSelectedStyle,
    optionTemplate,
    optionListTemplate,
    dropDownDataLoaded,
    pageSize = 50,
    showAddNewOption = false,
    debounceTime = 1000,
    allowDuplicateCall,
    filterInputRef,
    onNewItemCreated,
    showDataOnSearch,
    minimumSearchCharacters,
  } = props;

  const features = useRef(new DropDownFeatures(filterSignal));

  const handleActionItemChanged = (item: ActionSectionItem) => {
    const itemData: ActionSectionItemData = {
      id,
      ...item,
    };

    onActionItemChanged?.(itemData, features.current);
  };

  return (
    <div className="odin-w-full odin-select-none odin-border-b odin-border-l odin-border-r odin-border-gray-4 odin-bg-mono-1 odin-text-sm">
      {allowSearch && (
        <div className="odin-flex odin-w-full odin-bg-gray-5 odin-px-2 odin-py-4">
          <div className="odin-min-w-0 odin-grow">
            <DataFilter
              id={id}
              ref={filterInputRef}
              field={textField}
              debounceTime={debounceTime}
              placeholder={placeholder}
              filterSignal={filterSignal}
            />
          </div>
        </div>
      )}
      {actionItems && (
        <div className="odin-w-full odin-bg-gray-5 odin-px-2 odin-py-1">
          <DropDownActionSection items={actionItems} onItemChanged={handleActionItemChanged} />
        </div>
      )}
      <DropDownOptionList
        ref={ref}
        id={id}
        textField={textField}
        valueField={valueField}
        archivedField={archivedField}
        data={data}
        value={value}
        setDefaultValue={setDefaultValue}
        selectionType={selectionType}
        useCheckboxForMultiSelect={useCheckboxForMultiSelect}
        removeOnSelect={removeOnSelect}
        isListOpen={isListOpen}
        itemsToRemove={itemsToRemove}
        filterSignal={filterSignal}
        filterLocation={filterLocation}
        registerSignal={registerSignal}
        dispatcher={dispatcher}
        onChange={onChange}
        onDoubleClick={onDoubleClick}
        optionListStyle={optionListStyle}
        optionHoverStyle={optionHoverStyle}
        optionSelectedStyle={optionSelectedStyle}
        optionTemplate={optionTemplate}
        optionListTemplate={optionListTemplate}
        dropDownDataLoaded={dropDownDataLoaded}
        pageSize={pageSize}
        showAddNewOption={showAddNewOption}
        allowDuplicateCall={allowDuplicateCall}
        onNewItemCreated={onNewItemCreated}
        showDataOnSearch={showDataOnSearch}
        minimumSearchCharacters={minimumSearchCharacters}
        actionItems={actionItems}
      />
    </div>
  );
};

export default forwardRef(DropDownOptionWrapper);
